import { useLayoutEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setDialogOpen } from '../../editorSlice'
import type { SettingsPageState } from '../course-settings/settingsPageState'
import { useHasUnsavedBlockDraft } from './aiSidebar/useUnsavedBlockGate'

/**
 * На телефоне настройки — страницей, как с карточки на главной: окно поверх редактора там
 * тесное. Настройки открывают из разных мест (шестерёнка, «Перейти» в списке правок, стиль
 * картинок), поэтому перехватываем само открытие окна: вкладка и язык едут на страницу.
 * Несохранённую правку экрана уход со страницы потерял бы (защиты от ухода у редактора
 * нет) — тогда остаётся окно. `useLayoutEffect`: окно не успевает мелькнуть.
 */
export const useSettingsPageOnPhone = (courseId: number): void => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const hasUnsavedDraft = useHasUnsavedBlockDraft()
  const open = useAppSelector((s) => s.editor.dialogs.settingsOpen)
  const tab = useAppSelector((s) => s.editor.settingsTab)
  const lang = useAppSelector((s) => s.editor.settingsLang)
  // Телефон на фронте открытия окна: сузили окно браузера под открытыми настройками —
  // окно остаётся, автора со страницы настроек не уводит на другую страницу посреди работы.
  const mobileAtOpen = useRef(false)
  const wasOpen = useRef(false)
  if (open && !wasOpen.current) mobileAtOpen.current = isMobile
  wasOpen.current = open
  useLayoutEffect(() => {
    if (!open || !isMobile || !mobileAtOpen.current || hasUnsavedDraft) return
    const state: SettingsPageState = { from: 'editor', tab, lang }
    dispatch(setDialogOpen({ dialog: 'settingsOpen', open: false }))
    navigate(`/course-settings/${courseId}`, { state })
  }, [open, isMobile, hasUnsavedDraft, tab, lang, courseId, dispatch, navigate])
}
